import ShopOutlineIcon from "@src/lib/icons/18px/shop-outline";
import StudentIcon from "@src/lib/icons/18px/student";
import BookOpenOutlineIcon from "@src/lib/icons/18px/book-open-outline";
import BookmarkOutlineIcon from "@src/lib/icons/18px/bookmark-outline";
import CircleCheckmarkOutlineIcon from "@src/lib/icons/18px/circle-checkmark-outline";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import OfficeOutlineIcon from "@src/lib/icons/18px/office-outline";
import TagOutlineIcon from "@src/lib/icons/18px/tag-outline";
import FileSendOutlineIcon from "@src/lib/icons/18px/file-send-outline";
import GearOutlineIcon from "@src/lib/icons/18px/gear-outline";
import GlobeIcon from "@src/lib/icons/18px/globe";
import WindowChartLineOutlineIcon from "@src/lib/icons/18px/window-chart-line-outline";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { useIsStaff } from "@src/features/app/hooks/useIsStaff";
import useUser from "@src/features/app/hooks/useUser";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useMemo } from "react";
import useLocationGroups from "@src/features/locations/hooks/useLocationGroups";
import { Ff, LocationGroupType } from "@src/types.generated";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import { buildJumpToRouteItemsFromAdminNav } from "./jumpToAdminNavRouteItems";
import { ActionItem, RouteItem } from "./useAllJumpToItems";

export const ADMIN_TOOLS_JUMP_TO_ACTION_ID = "admin-tools";

const buildAdminToolsActionItem = (): ActionItem => ({
  id: ADMIN_TOOLS_JUMP_TO_ACTION_ID,
  name: "Admin tools",
  type: "action",
  action: "admin-tools",
  icon: GlobeIcon,
});

/** Shown in the cmd+k "Go to" section when the search box is empty. */
export const defaultRouteItemIds = [
  "home",
  "training",
  "reporting",
  "messages",
  "people-users",
];

const SUPPLEMENTAL_ROUTE_ITEMS: RouteItem[] = [
  {
    id: "marketplace",
    name: "Marketplace",
    route: { pathname: "/marketplace" },
    type: "route",
    icon: ShopOutlineIcon,
  },
  {
    id: "my-training",
    name: "My Training",
    route: { pathname: "/my-training" },
    type: "route",
    icon: StudentIcon,
  },
  {
    id: "library-modules",
    name: "Modules",
    route: { pathname: "/library", query: { type: "modules" } },
    type: "route",
    icon: BookOpenOutlineIcon,
  },
  {
    id: "library-courses",
    name: "Courses",
    route: { pathname: "/library", query: { type: "courses" } },
    type: "route",
    icon: BookmarkOutlineIcon,
  },
  {
    id: "library-check-ins",
    name: "Check-ins",
    route: { pathname: "/library", query: { type: "check-ins" } },
    type: "route",
    icon: CircleCheckmarkOutlineIcon,
  },
  {
    id: "library-resources",
    name: "Resources",
    route: { pathname: "/library", query: { type: "resources" } },
    type: "route",
    icon: FileTextOutlineIcon,
  },
  {
    id: "locations-franchises",
    name: "Franchises",
    route: { pathname: "/locations/franchises" },
    type: "route",
    icon: OfficeOutlineIcon,
  },
  {
    id: "locations-brands",
    name: "Brands",
    route: { pathname: "/locations/brands" },
    type: "route",
    icon: OfficeOutlineIcon,
  },
  {
    id: "locations-regions",
    name: "Regions",
    route: { pathname: "/locations/regions" },
    type: "route",
    icon: OfficeOutlineIcon,
  },
  {
    id: "tags",
    name: "Tags",
    route: { pathname: "/settings/tags" },
    type: "route",
    icon: TagOutlineIcon,
  },
  {
    id: "access",
    name: "Access",
    route: { pathname: "/access" },
    type: "route",
    icon: FileSendOutlineIcon,
  },
  {
    id: "reporting-builder",
    name: "Report Builder",
    route: { pathname: "/reporting/builder" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-compliance-all",
    name: "Reporting > Compliance",
    route: { pathname: "/reporting/compliance/all" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-course-feedback",
    name: "Reporting > Course Feedback",
    route: { pathname: "/reporting/course-feedback" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-location-completion",
    name: "Reporting > Location Completion Grid",
    route: { pathname: "/reporting/location-completion-grid" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-user-completion",
    name: "Reporting > User Completion Grid",
    route: { pathname: "/reporting/user-completion-grid" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-message-reads",
    name: "Reporting > Message Engagement",
    route: { pathname: "/reporting/message-reads-grid" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-location-leaderboard",
    name: "Location Leaderboard",
    route: { pathname: "/reporting/location-leaderboard" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-manager-leaderboard",
    name: "Reporting > Manager Leaderboard",
    route: { pathname: "/reporting/manager-leaderboard" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "reporting-trainee-leaderboard",
    name: "Reporting > Trainee Leaderboard",
    route: { pathname: "/reporting/trainee-leaderboard" },
    type: "route",
    icon: WindowChartLineOutlineIcon,
  },
  {
    id: "settings-billing",
    name: "Settings > Billing",
    route: { pathname: "/settings/billing" },
    type: "route",
    icon: GearOutlineIcon,
  },
];

const filterSupplementalRouteItems = (
  items: RouteItem[],
  options: {
    locationGroupTypes: LocationGroupType[];
    userActionPermissions: ReturnType<
      typeof useActionPermissions
    >["userActionPermissions"];
    billingEnabled: boolean;
  },
): RouteItem[] => {
  const { locationGroupTypes, userActionPermissions, billingEnabled } = options;
  return items.filter((item) => {
    switch (item.id) {
      case "locations-franchises":
        return locationGroupTypes.includes(LocationGroupType.Franchise);
      case "locations-brands":
        return locationGroupTypes.includes(LocationGroupType.Brand);
      case "settings-billing":
        return billingEnabled && userActionPermissions?.manageBilling;
      // /access requires manageLibrary; without it the page 404s.
      case "access":
        return userActionPermissions?.manageLibrary;
      case "reporting-builder":
      case "reporting-compliance-all":
      case "reporting-course-feedback":
      case "reporting-location-completion":
      case "reporting-user-completion":
      case "reporting-message-reads":
      case "reporting-location-leaderboard":
      case "reporting-manager-leaderboard":
      case "reporting-trainee-leaderboard":
        return userActionPermissions?.viewReporting;
      default:
        return true;
    }
  });
};

export const useFilteredRouteItems = (): Array<RouteItem | ActionItem> => {
  const { userActionPermissions } = useActionPermissions();
  const { isStaff } = useIsStaff();
  const { user } = useUser();
  const { locationGroupTypes } = useLocationGroups();
  const billingEnabled = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });
  const opusAgentEnabled = useFeatureFlag(Ff.OpusAgent);
  const auditsEnabled = useFeatureFlag(Ff.Audits);
  const taskLibraryItemTypeEnabled = useFeatureFlag(Ff.TaskLibraryItemType);
  const guestFeedbackEnabled = useFeatureFlag(Ff.GuestFeedbackCorrectiveAction);
  const guestFeedbackRecommendationsEnabled = useFeatureFlag(
    Ff.GuestFeedbackRecommendations,
  );
  const newMessagingEnabled = useFeatureFlag(Ff.NewMessaging);
  const pathsAsWorkflowsEnabled = useFeatureFlag(Ff.PathsAsWorkflows);
  const workflowsTabEnabled = useFeatureFlag(Ff.WorkflowsTab);

  const visibilityContext = useMemo(
    () => ({
      permissions: userActionPermissions,
      featureFlags: {
        [Ff.OpusAgent]: opusAgentEnabled,
        [Ff.Audits]: auditsEnabled,
        [Ff.TaskLibraryItemType]: taskLibraryItemTypeEnabled,
        [Ff.GuestFeedbackCorrectiveAction]: guestFeedbackEnabled,
        [Ff.GuestFeedbackRecommendations]: guestFeedbackRecommendationsEnabled,
        [Ff.NewMessaging]: newMessagingEnabled,
        [Ff.PathsAsWorkflows]: pathsAsWorkflowsEnabled,
        [Ff.WorkflowsTab]: workflowsTabEnabled,
      },
      userType: user?.userType,
    }),
    [
      opusAgentEnabled,
      auditsEnabled,
      taskLibraryItemTypeEnabled,
      guestFeedbackEnabled,
      guestFeedbackRecommendationsEnabled,
      newMessagingEnabled,
      pathsAsWorkflowsEnabled,
      workflowsTabEnabled,
      userActionPermissions,
      user?.userType,
    ],
  );

  const navRouteItems = useMemo(
    () => buildJumpToRouteItemsFromAdminNav(visibilityContext),
    [visibilityContext],
  );

  const supplementalRouteItems = useMemo(
    () =>
      filterSupplementalRouteItems(SUPPLEMENTAL_ROUTE_ITEMS, {
        locationGroupTypes,
        userActionPermissions,
        billingEnabled,
      }).map((item) =>
        newMessagingEnabled && item.id === "reporting-message-reads"
          ? { ...item, name: "Reporting > Newsfeed Engagement" }
          : item,
      ),
    [
      billingEnabled,
      locationGroupTypes,
      userActionPermissions,
      newMessagingEnabled,
    ],
  );

  return useMemo(
    () => [
      ...navRouteItems,
      ...supplementalRouteItems,
      ...(isStaff ? [buildAdminToolsActionItem()] : []),
    ],
    [isStaff, navRouteItems, supplementalRouteItems],
  );
};
